import { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, X } from 'lucide-react';
import orchardService from '../../services/orchard';
import PbrMark from '../cultivar/PbrMark';

export interface PickedCultivar {
  id: number;
  name: string;
  trade_name: string | null;
  crop_type_name: string | null;
  protection_status?: string | null;
}

interface CultivarPickerProps {
  label: string;
  hint?: string;
  placeholder?: string;
  /** Restrict the search to one registry role. Omit to search every cultivar. */
  cultivarRole?: 'scion' | 'rootstock';
  /** Currently selected id, if any. */
  value?: number | null;
  /** Name to show when the form loads with a selection already made. */
  initialDisplayName?: string | null;
  onChange: (cultivar: PickedCultivar | null) => void;
  error?: string;
}

const displayName = (c: PickedCultivar) =>
  c.trade_name ? `${c.name} (${c.trade_name})` : c.name;

/**
 * Search-and-select over the cultivar registry, returning the whole row rather
 * than just a name. Used for the rootstock and interstem of a composite tree —
 * both are cultivars in their own right and may be separately protected, so
 * storing the id is what lets the protection status be resolved later.
 */
function CultivarPicker({
  label,
  hint,
  placeholder = 'Search cultivars...',
  cultivarRole,
  value,
  initialDisplayName,
  onChange,
  error,
}: CultivarPickerProps) {
  const [query, setQuery] = useState(initialDisplayName || '');
  const [results, setResults] = useState<PickedCultivar[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const search = useCallback(async (q: string) => {
    if (q.length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await orchardService.searchCultivars(q, 10, cultivarRole);
      if (response.success && response.data) {
        setResults(response.data.cultivars);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [cultivarRole]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, open, search]);

  const select = (c: PickedCultivar) => {
    setQuery(displayName(c));
    setOpen(false);
    onChange(c);
  };

  const clear = () => {
    setQuery('');
    setResults([]);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-neutral-700">{label}</label>
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            className="input-professional w-full pl-9 pr-9"
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              // Typing over a selection unselects it — the id must never
              // survive an edit to the text it was chosen from.
              if (value) onChange(null);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-neutral-400" />
          )}
          {!loading && query && (
            <button
              type="button"
              onClick={clear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              aria-label={`Clear ${label}`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {open && results.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                className="w-full text-left px-4 py-2.5 hover:bg-primary-50 focus:bg-primary-50 text-sm border-b border-neutral-100 last:border-0"
                onClick={() => select(c)}
              >
                <div className="font-medium text-neutral-900 flex items-center gap-1.5">
                  {c.name}
                  <PbrMark status={c.protection_status} />
                </div>
                {c.trade_name && (
                  <div className="text-xs text-neutral-500">{c.trade_name}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {value ? (
        <p className="text-xs text-success-600">✓ Selected from the registry</p>
      ) : query ? (
        <p className="text-xs text-amber-700">
          Not linked to the registry — pick from the list so protection status can be checked
        </p>
      ) : null}
      {error && <p className="text-xs text-error-600">{error}</p>}
    </div>
  );
}

export default CultivarPicker;
